import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  CreditCard,
  Plus,
  Tag,
  Trash2,
  Truck,
} from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`)) {
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function CheckoutFlow() {
  const { cart, refresh, loading: cartLoading } = useCart();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [coupon, setCoupon] = useState({ code: "", applied: null });
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jow_token");
    if (!token) return;
    api
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem("jow_token");
        window.location.href = "/login?next=/checkout";
      });
  }, []);

  const reloadAddresses = async () => {
    try {
      const { data } = await api.get("/addresses");
      setAddresses(data.addresses || []);
      setSelectedAddrId((curr) => {
        if (curr) return curr;
        const def = (data.addresses || []).find((a) => a.is_default);
        if (def) return def.id;
        return data.addresses?.[0]?.id || null;
      });
    } catch (e) {
      // not signed in
    }
  };

  useEffect(() => {
    if (me) reloadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (!localStorage.getItem("jow_token")) {
    return <Navigate to={`/login?next=/checkout`} replace />;
  }

  if (cartLoading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center label-overline text-jow-navy/55" data-testid="checkout-loading">
        Preparing checkout…
      </div>
    );
  }

  if (cart.items.length === 0) {
    return <Navigate to="/cart" replace />;
  }

  const applyCoupon = async () => {
    if (!coupon.code.trim()) return;
    try {
      const { data } = await api.post("/coupons/apply", {
        code: coupon.code.trim(),
        cart_subtotal: cart.subtotal,
      });
      if (!data.valid) {
        toast.error(data.message);
        setCoupon((c) => ({ ...c, applied: null }));
        return;
      }
      setCoupon({ code: data.code, applied: data });
      toast.success(`Coupon applied — saved ₹${data.discount_amount}`);
    } catch (e) {
      toast.error("Could not apply coupon.");
    }
  };

  const removeCoupon = () => setCoupon({ code: "", applied: null });

  const subtotal = cart.subtotal;
  const discount = coupon.applied?.discount_amount || 0;
  const taxable = Math.max(0, subtotal - discount);
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 99;
  const tax = Math.round(taxable * 0.03);
  const total = taxable + shipping + tax;

  const placeOrder = async () => {
    if (!selectedAddrId) {
      toast.error("Please choose a delivery address.");
      return;
    }
    setPlacing(true);
    try {
      const body = {
        address_id: selectedAddrId,
        payment_method: paymentMethod,
      };
      if (coupon.applied) body.coupon_code = coupon.applied.code;

      const { data } = await api.post("/orders/create", body);

      if (paymentMethod === "cod") {
        await refresh();
        navigate(`/order/${data.order_no}/success`, { replace: true });
        return;
      }

      // razorpay path
      if (data.mock_mode) {
        toast.warning("Razorpay test mode — order saved as pending.");
        await refresh();
        navigate(`/order/${data.order_no}/success?mock=1`, { replace: true });
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load Razorpay.");
        return;
      }
      const opts = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "JewelleryOnWheels",
        description: `Order ${data.order_no}`,
        order_id: data.razorpay_order_id,
        prefill: {
          name: me?.name || "JOW Customer",
          email: me?.email || "",
          contact: me?.phone || "",
        },
        theme: { color: "#0A1F44" },
        handler: async (resp) => {
          try {
            await api.post(`/orders/${data.order_no}/payment-callback`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            navigate(`/order/${data.order_no}/success`, { replace: true });
          } catch (err) {
            toast.error("Payment verification failed.");
          }
        },
        modal: {
          ondismiss: () => toast.info("Payment dismissed. Your order is pending."),
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="checkout-flow">
      <div className="mb-10">
        <span className="label-overline text-jow-gold">Checkout</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy">
          One last polish before it&rsquo;s yours.
        </h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-8 space-y-8">
          <Step number="01" label="Contact" testid="step-contact">
            <div className="flex items-center justify-between">
              <p className="text-jow-navy">
                Signed in as <strong>{me?.phone || "…"}</strong>
              </p>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem("jow_token");
                  window.location.href = "/login?next=/checkout";
                }}
                className="label-overline text-jow-pink hover:underline"
                data-testid="checkout-change-account"
              >
                Change
              </button>
            </div>
          </Step>

          <Step number="02" label="Delivery address" testid="step-address">
            <ul className="space-y-3">
              {addresses.map((a) => (
                <li key={a.id} data-testid={`addr-${a.id}`}>
                  <label
                    className={`flex items-start gap-3 p-4 border cursor-pointer ${
                      selectedAddrId === a.id
                        ? "border-jow-pink bg-jow-pink/5"
                        : "border-jow-navy/15 bg-white hover:border-jow-gold"
                    }`}
                  >
                    <input
                      type="radio"
                      name="addr"
                      checked={selectedAddrId === a.id}
                      onChange={() => setSelectedAddrId(a.id)}
                      className="mt-1 accent-jow-pink"
                      data-testid={`addr-radio-${a.id}`}
                    />
                    <div className="flex-1 text-sm">
                      <p className="font-display text-base text-jow-navy">
                        {a.label} · {a.name}
                      </p>
                      <p className="text-jow-navy/70 mt-1">
                        {a.line1}
                        {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}
                      </p>
                      <p className="text-jow-navy/55 mt-1 text-xs">{a.phone}</p>
                    </div>
                    {a.is_default && <span className="label-overline text-jow-gold">Default</span>}
                  </label>
                </li>
              ))}
            </ul>
            {!showAddrForm && (
              <button
                type="button"
                onClick={() => setShowAddrForm(true)}
                className="mt-4 inline-flex items-center gap-2 label-overline text-jow-pink"
                data-testid="addr-add-toggle"
              >
                <Plus size={14} /> Add new address
              </button>
            )}
            {showAddrForm && (
              <AddressForm
                onCancel={() => setShowAddrForm(false)}
                onSaved={async (newId) => {
                  setShowAddrForm(false);
                  await reloadAddresses();
                  if (newId) setSelectedAddrId(newId);
                }}
              />
            )}
          </Step>

          <Step number="03" label="Order summary" testid="step-summary">
            <ul className="divide-y divide-jow-navy/10" data-testid="checkout-items">
              {cart.items.map((it) => (
                <li
                  key={it.sku}
                  className="py-4 flex items-center gap-4"
                  data-testid={`co-item-${it.sku}`}
                >
                  <img src={it.image} alt={it.name} className="w-16 h-16 object-cover" />
                  <div className="flex-1">
                    <p className="font-display text-base text-jow-navy">{it.name}</p>
                    <p className="text-xs text-jow-navy/55">
                      Qty {it.qty} · {it.attrs?.color}
                    </p>
                  </div>
                  <span className="text-sm text-jow-navy">
                    ₹{it.line_total.toLocaleString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              {coupon.applied ? (
                <div
                  className="flex items-center justify-between border border-jow-gold/40 bg-jow-cream px-4 py-3"
                  data-testid="coupon-applied"
                >
                  <span className="text-sm flex items-center gap-2">
                    <Tag size={14} className="text-jow-gold" />
                    <strong>{coupon.applied.code}</strong> · saved ₹{coupon.applied.discount_amount}
                  </span>
                  <button
                    onClick={removeCoupon}
                    className="text-jow-pink"
                    data-testid="coupon-remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex border border-jow-navy/15 bg-white" data-testid="coupon-input-row">
                  <input
                    placeholder="WELCOME10 / FESTIVE500 / RIKSHAW100"
                    value={coupon.code}
                    onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })}
                    className="flex-1 px-4 py-3 text-sm outline-none"
                    data-testid="coupon-input"
                  />
                  <button
                    onClick={applyCoupon}
                    className="px-5 bg-jow-navy text-jow-cream text-xs uppercase tracking-widest"
                    data-testid="coupon-apply"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          </Step>

          <Step number="04" label="Payment" testid="step-payment">
            <div className="space-y-3">
              <PaymentOption
                value="razorpay"
                checked={paymentMethod === "razorpay"}
                onChange={setPaymentMethod}
                label="Pay online (Razorpay)"
                desc="Cards · UPI · Netbanking · Wallets"
                Icon={CreditCard}
                testid="pay-razorpay"
              />
              <PaymentOption
                value="cod"
                checked={paymentMethod === "cod"}
                onChange={setPaymentMethod}
                label="Cash on Delivery"
                desc="Pay the rikshaw driver when your piece arrives"
                Icon={Truck}
                testid="pay-cod"
              />
            </div>
            {paymentMethod === "razorpay" && (
              <p className="mt-4 text-xs text-jow-navy/55" data-testid="rzp-test-mode-note">
                Backend is running with placeholder Razorpay keys; order will be created in
                <span className="text-jow-pink"> mock mode</span> until real keys are configured.
              </p>
            )}
          </Step>
        </div>

        <aside
          className="lg:col-span-4 bg-jow-cream border border-jow-gold/40 p-6 md:p-8 sticky top-24 self-start"
          data-testid="checkout-totals"
        >
          <p className="label-overline text-jow-gold">Total</p>
          <div className="mt-5 space-y-3 text-sm">
            <Row label={`Subtotal (${cart.count} items)`} value={`₹${subtotal.toLocaleString("en-IN")}`} testid="ct-subtotal" />
            {discount > 0 && (
              <Row
                label={`Discount (${coupon.applied?.code})`}
                value={<span className="text-jow-pink">−₹{discount.toLocaleString("en-IN")}</span>}
                testid="ct-discount"
              />
            )}
            <Row label="Shipping" value={shipping === 0 ? "Free" : `₹${shipping}`} testid="ct-shipping" />
            <Row label="GST (3%)" value={`₹${tax.toLocaleString("en-IN")}`} testid="ct-tax" />
            <div className="border-t border-jow-navy/10 pt-3 mt-3">
              <Row
                label={<span className="font-display text-lg">Total</span>}
                value={<span className="font-display text-lg">₹{total.toLocaleString("en-IN")}</span>}
                testid="ct-total"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={placeOrder}
            disabled={placing || !selectedAddrId}
            className="btn-primary-jow w-full mt-7"
            data-testid="place-order-btn"
          >
            {placing ? "Placing…" : (
              <>
                Place Order <ChevronRight size={14} />
              </>
            )}
          </button>
          <p className="mt-3 text-[10px] text-jow-navy/45 leading-relaxed">
            By placing this order you agree to our terms and privacy policy.
          </p>
        </aside>
      </div>
    </div>
  );
}

function Step({ number, label, children, testid }) {
  return (
    <section className="bg-white border border-jow-navy/10 p-6 md:p-8" data-testid={testid}>
      <header className="flex items-center gap-4 mb-5">
        <span className="font-display text-2xl text-jow-gold">{number}</span>
        <h2 className="font-display text-xl text-jow-navy">{label}</h2>
      </header>
      {children}
    </section>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-jow-navy/65">{label}</span>
      <span className="text-jow-navy">{value}</span>
    </div>
  );
}

function PaymentOption({ value, checked, onChange, label, desc, Icon, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-4 border cursor-pointer ${
        checked ? "border-jow-pink bg-jow-pink/5" : "border-jow-navy/15 bg-white hover:border-jow-gold"
      }`}
    >
      <input
        type="radio"
        name="pay"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1 accent-jow-pink"
        data-testid={testid}
      />
      <Icon size={18} className="mt-0.5 text-jow-gold" />
      <div className="flex-1 text-sm">
        <p className="font-display text-base text-jow-navy">{label}</p>
        <p className="text-jow-navy/55 mt-1 text-xs">{desc}</p>
      </div>
    </label>
  );
}

function AddressForm({ onCancel, onSaved }) {
  const [form, setForm] = useState({
    label: "Home",
    name: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    is_default: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[1-9]\d{5}$/.test(form.pincode)) {
      toast.error("Invalid PIN code.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/addresses", form);
      toast.success("Address saved.");
      onSaved?.(data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 border border-jow-navy/10 bg-jow-cream p-5"
      data-testid="addr-form"
    >
      <input className="input-jow border-b" placeholder="Label" value={form.label} onChange={set("label")} data-testid="addr-label" />
      <input className="input-jow border-b" placeholder="Full name" value={form.name} onChange={set("name")} required data-testid="addr-name" />
      <input className="input-jow border-b" placeholder="+91 …" value={form.phone} onChange={set("phone")} required data-testid="addr-phone" />
      <input className="input-jow border-b" placeholder="PIN code" value={form.pincode} onChange={set("pincode")} required data-testid="addr-pincode" />
      <input className="input-jow border-b md:col-span-2" placeholder="Address line 1" value={form.line1} onChange={set("line1")} required data-testid="addr-line1" />
      <input className="input-jow border-b md:col-span-2" placeholder="Address line 2 (optional)" value={form.line2} onChange={set("line2")} data-testid="addr-line2" />
      <input className="input-jow border-b" placeholder="City" value={form.city} onChange={set("city")} required data-testid="addr-city" />
      <input className="input-jow border-b" placeholder="State" value={form.state} onChange={set("state")} required data-testid="addr-state" />
      <label className="flex items-center gap-2 text-sm text-jow-navy/70 md:col-span-2">
        <input
          type="checkbox"
          checked={form.is_default}
          onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
          className="accent-jow-pink"
        />
        Set as default
      </label>
      <div className="md:col-span-2 flex gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary-jow flex-1" data-testid="addr-cancel">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-primary-jow flex-1" data-testid="addr-save">
          {busy ? "Saving…" : "Save address"}
        </button>
      </div>
    </form>
  );
}
